import { useRef, useState, useEffect, useCallback } from "react";
import { Loader2, Eraser, PenLine, Check } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";

// Vor-Ort-Unterschrift (Bildschirm/Tablet). Erfasst 1–2 Unterschriften auf
// Canvas und liefert sie als PNG-Data-URLs zurück.
function SignatureCanvas({ label, testid, onChange }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const dirty = useRef(false);
    const last = useRef({ x: 0, y: 0 });

    const pos = (e) => {
        const c = canvasRef.current;
        const rect = c.getBoundingClientRect();
        const t = e.touches ? e.touches[0] : e;
        return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
    const move = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const c = canvasRef.current; const ctx = c.getContext("2d");
        const p = pos(e);
        ctx.strokeStyle = "#111827"; ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round";
        ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
        last.current = p; dirty.current = true;
    };
    const end = () => {
        if (!drawing.current) return;
        drawing.current = false;
        if (dirty.current) onChange(canvasRef.current.toDataURL("image/png"));
    };
    const clear = () => {
        const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height);
        dirty.current = false; onChange("");
    };

    useEffect(() => {
        const c = canvasRef.current;
        if (c) { c.width = 600; c.height = 160; }
    }, []);

    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-600">{label}</Label>
                <button type="button" onClick={clear} className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-500 hover:text-red-600" data-testid={`${testid}-clear`}>
                    <Eraser className="w-3.5 h-3.5" /> Löschen
                </button>
            </div>
            <canvas
                ref={canvasRef}
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                className="w-full h-[110px] bg-white border-2 border-dashed border-gray-300 rounded-sm touch-none cursor-crosshair"
                data-testid={testid}
            />
        </div>
    );
}

export function OnSiteSignatureModal({ title = "Vor-Ort-Unterschrift", fields = [], busy = false, defaultPlace = "", onClose, onSubmit }) {
    const [sigs, setSigs] = useState({});
    const [place, setPlace] = useState(defaultPlace);
    const setSig = useCallback((k, v) => setSigs((p) => ({ ...p, [k]: v })), []);
    const hasAny = fields.some((f) => sigs[f.key]);

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="max-w-lg" data-testid="dispo-onsite-signature-modal">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><PenLine className="w-5 h-5 text-orange-600" /> {title}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-600">Ort</Label>
                        <Input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="z. B. Berlin" className="h-9 text-sm" data-testid="dispo-onsite-place" />
                    </div>
                    {fields.map((f) => (
                        <SignatureCanvas key={f.key} label={f.label} testid={`dispo-onsite-canvas-${f.key}`} onChange={(v) => setSig(f.key, v)} />
                    ))}
                    <p className="text-[11px] text-gray-500">Mit Maus, Finger oder Stift unterschreiben. Die Unterschrift wird direkt in das Vertrags-PDF eingebettet.</p>
                </div>
                <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button type="button" onClick={() => onSubmit(sigs, place)} disabled={busy || !hasAny} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="dispo-onsite-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Unterschrift übernehmen</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default OnSiteSignatureModal;
